import { createContext, useEffect, useState, type ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { resolveNotificationsHubUrl } from '../lib/signalRHub';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from './WorkspaceContext';

function NotificationAction({ path }: { path?: string }) {
  const { t } = useTranslation();
  if (!path || !/^\/dashboard(?:[/?#]|$)/.test(path) || /[\\\r\n]/.test(path)) return null;
  return <Link to={path} className="mt-2 text-sm font-semibold text-cyan-300 underline">{t('renewalAlerts.openRelated')}</Link>;
}

export const NotificationContext = createContext<{ connection: signalR.HubConnection | null }>({
  connection: null,
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const { access } = useWorkspace();
  const [hubConnection, setHubConnection] = useState<signalR.HubConnection | null>(null);

  useEffect(() => {
    if (!isAuthenticated || access?.role !== 'Owner') {
      setHubConnection((prev) => {
        void prev?.stop();
        return null;
      });
      return;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(resolveNotificationsHubUrl(), {
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .build();

    connection.on('ReceiveNotification', (notification: { type?: string; title?: string; message?: string; actionPath?: string }) => {
      const { type, title, message } = notification;

      const toastOptions = {
        duration: 6000,
        position: 'bottom-right' as const,
        style: {
          background: '#1F2937',
          color: '#fff',
          border: '1px solid #374151',
          maxWidth: '400px',
        },
      };

      if (type === 'Success') {
        toast.success(
          <div className="flex flex-col">
            <strong className="font-semibold text-emerald-400">{title}</strong>
            <div className="mt-1 text-sm font-normal text-gray-300">{message}</div>
            <NotificationAction path={notification.actionPath} />
          </div>,
          toastOptions,
        );
      } else if (type === 'Error') {
        toast.error(
          <div className="flex flex-col">
            <strong className="font-semibold text-rose-400">{title}</strong>
            <div className="mt-1 text-sm font-normal text-gray-300">{message}</div>
            <NotificationAction path={notification.actionPath} />
          </div>,
          toastOptions,
        );
      } else {
        toast(
          <div className="flex flex-col">
            <strong className="font-semibold text-white">{title}</strong>
            <div className="mt-1 text-sm font-normal text-gray-300">{message}</div>
            <NotificationAction path={notification.actionPath} />
          </div>,
          toastOptions,
        );
      }
    });

    let cancelled = false;

    connection
      .start()
      .then(() => {
        if (!cancelled) {
          console.log('SignalR Notification Hub connected');
          setHubConnection(connection);
        } else {
          void connection.stop();
        }
      })
      .catch((err) => console.error('SignalR Connection Error:', err));

    return () => {
      cancelled = true;
      connection.off('ReceiveNotification');
      void connection.stop();
      setHubConnection(null);
    };
  }, [isAuthenticated, access?.workspaceId, access?.role]);

  return (
    <NotificationContext.Provider value={{ connection: hubConnection }}>
      {children}
      <Toaster />
    </NotificationContext.Provider>
  );
};
